/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return -- Route handling requires dynamic access to meta objects */
import type { RouteLocationNormalized } from 'vue-router'

export function getMetaFromRoute(nextRoute: RouteLocationNormalized) {
  let routeMeta =
    nextRoute.meta && Array.isArray(nextRoute.meta)
      ? (nextRoute.meta || [{}]).reduce(
          (acc: any, obj: any) => Object.assign(acc, obj),
          {},
        )
      : nextRoute.meta || {}
  // for some reason meta is not always on the component
  // but can be on the matched component default options
  // so let's merge that in
  const firstMatch = nextRoute.matched?.[0]
  const defaultComponent = firstMatch?.components?.default
  const matchedMeta =
    defaultComponent && 'options' in defaultComponent
      ? (defaultComponent as any).options?.meta || {}
      : {}
  routeMeta = { ...routeMeta, ...matchedMeta }
  return routeMeta
}

/**
 * Generates a URL with the specified locale prefix
 * @param href The URL or path to localize
 * @param locale The target locale to use for the prefix
 * @returns URL with the locale prefix
 */
function generateLocalisedUrl(href: string, locale: string): string {
  const urlWithNoLeadingSlash = href.replace(/^\/+/g, '')
  return `/${locale}/${urlWithNoLeadingSlash}`
}

/**
 * Determines if and how to apply locale prefix to a URL
 * @param params Object containing URL and locale information
 * @returns URL with appropriate locale handling
 */
function getUrl({
  currentUrl,
  targetLocale,
  defaultLocale,
}: {
  currentUrl: string
  targetLocale: string
  defaultLocale: string
}): string {
  // If URL is not a path (has protocol), return as is
  if (currentUrl.includes('://')) {
    return currentUrl
  }

  const targetLocalePart =
    targetLocale === defaultLocale ? undefined : `/${targetLocale}/`

  if (
    currentUrl.startsWith('/') &&
    targetLocalePart &&
    !currentUrl.startsWith(targetLocalePart)
  ) {
    return generateLocalisedUrl(currentUrl, targetLocale)
  }

  return currentUrl
}

/**
 * Ensures a URL has the correct locale prefix
 * @param route The URL string or route object to localize
 * @param locale The target locale to use
 * @param defaultLocale The default locale (which doesn't need prefix)
 * @param router Optional router instance (required when passing a route object)
 * @returns The URL with the appropriate locale prefix
 */
export function localePath(
  route: string | Record<string, any>,
  locale: string,
  router?: any,
  defaultLocale: string = 'en-gb',
): string {
  // If route is a string, use it directly
  if (typeof route === 'string') {
    return getUrl({
      currentUrl: route,
      targetLocale: locale,
      defaultLocale,
    })
  }

  // If route is an object but no router is provided, throw an error
  if (!router) {
    // eslint-disable-next-line no-console -- Defensive warning for debugging missing router
    console.warn('Router is required when passing a route object to localePath')
    return ''
  }

  try {
    // Resolve the route object to a URL using the router
    let currentUrl = router.resolve(route).href

    // Add trailing slash if not present (to match site convention and prevent tracking discrepancies)
    // Parse URL to handle query params and hash correctly
    const queryIndex = currentUrl.indexOf('?')
    const hashIndex = currentUrl.indexOf('#')
    const splitIndex =
      queryIndex !== -1 ? queryIndex : hashIndex !== -1 ? hashIndex : -1

    if (splitIndex !== -1) {
      // URL has query params or hash
      const pathPart = currentUrl.substring(0, splitIndex)
      const suffixPart = currentUrl.substring(splitIndex)

      // Add trailing slash to path if not present and not a file
      if (!pathPart.endsWith('/') && !pathPart.includes('.')) {
        currentUrl = `${pathPart}/${suffixPart}`
      }
    } else {
      // No query params or hash
      // Add trailing slash if not present and not a file
      if (!currentUrl.endsWith('/') && !currentUrl.includes('.')) {
        currentUrl = `${currentUrl}/`
      }
    }

    return getUrl({
      currentUrl,
      targetLocale: locale,
      defaultLocale,
    })
  } catch (error) {
    console.error('Error resolving route in localePath:', error)
    return ''
  }
}
